import { Box, Typography, Stack, useTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import HeurekaNotificationModal from "./HeurekaNotificationModal";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import { NotificationType } from "../../shared/types/notificationSettingsTypes";
import { WebsiteManager } from "../../services/api/WebsiteManager";
import { NotificationManager } from "../../services/api/NotificationManager";
import { WebsiteDto } from "../../shared/types/websiteTypes";
import { useDispatch, useSelector } from "react-redux";
import {
  getActiveWebId,
  getCustomWebLogo,
} from "../../services/selectors/web-selectors";
import ApiKeyModal from "../../components/ApiKeyModal";
import { HeurekaApiKeyModal } from "../OnBoarding/components/Modals/HeurekaApiKeyModal";
import ActualVisitorsModal from "./ActualVisitorsModal";
import CustomNotificationModal from "./CustomNotificationModal";
import SummaryModal from "./SummaryModal";
import ShoptetLastOrdersNotificationModal from "./ShoptetLastOrdersNotificationModal";
import ShopifyLastOrdersNotificationModal from "./ShopifyLastOrdersNotificationModal";
import EshopRychleLastOrdersNotificationModal from "./EshopRychleLastOrdersNotificationModal";
import OrdersAndRegistrationsModal from "./OrdersAndRegistrationsModal";
import { ZboziApiKeyModal } from "../OnBoarding/components/Modals/ZboziApiKeyModal";
import ZboziNotificationModal from "./ZboziNotificationModal";
import { NotificationsPreviewsStringsProps } from "../../components/types";
import {
  convertStringToLanguage,
  generateNotificationPreviews,
} from "../../generic-utils";
import { useTranslation } from "react-i18next";
import ShoptetCouponCodeNotificationModal from "./ShoptetCouponCodeNotificationModal";
import SpolehliveRecenzeNotificationModal from "./SpolehliveRecenzeNotificationModal";
import { useEditCampaign } from "../../context/EditCampaignContext";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CustomButton from "../../components/CustomButton";
import { useCampaign } from "../../context/CampaignContext";
import { useNotificationManager } from "../../hooks/useNotificationManager";
import { NotificationTypeSelector } from "../../components/notification/NotificationTypeSelector";
import GoogleReviewNotificationModal from "./GoogleReviewNotificationModal";
import { GoogleReviewApiKeyModal } from "../OnBoarding/components/Modals/GoogleReviewApiKeyModal";
import SpolehliveRecenzeLoginModal from "../OnBoarding/components/Modals/SpolehliveRecenzeLoginModal";
import TrustPilotNotificationModal from "./TrustPilotNotificationModal";
import { TrustPilotApiKeyModal } from "../OnBoarding/components/Modals/TrustPilotApiKeyModal";

const AddNotification = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const webId = useSelector(getActiveWebId);
  const customWebBranding = useSelector(getCustomWebLogo);
  const language = convertStringToLanguage(i18n.language);
  const [createdNotificationId, setCreatedNotificationId] =
    useState<string>("");
  const [websiteDto, setWebsiteDto] = useState<WebsiteDto | undefined>();
  const [isNotificationAdded, setIsNotificationAdded] =
    useState<boolean>(false);
  // Use ref instead of state to avoid React batching issues
  const wasNotificationSavedRef = useRef<boolean>(false);
  const theme = useTheme();
  const { detectedChange, setReset, setSave } = useEditCampaign();
  const {
    showIcon,
    activeSize,
    iconBackgroundColor,
    iconBackgroundRadius,
    iconColor,
    textColor,
    backgroundColor,
    backgroundOpacity,
    borderRadius,
  } = useCampaign();
  const { createNotificationPrimitive } = useNotificationManager();

  // API KEYS MODALS:
  const [apiKeyModalOpenFor, setApiKeyModalOpenFor] = useState<
    | "zbozi"
    | "heureka"
    | "spolehlivaRecenze"
    | "googleReviews"
    | "trustpilotReviews"
    | undefined
  >();
  const [isAddZboziApiKeyModalOpen, setIsAddZboziApiKeyModalOpen] =
    useState<boolean>(false);
  const [addZboziApiKeyModal, setAddZboziApiKeyModal] =
    useState<boolean>(false);
  const [
    isSpolehliveRecenzeLoginModalOpen,
    setIsSpolehliveRecenzeLoginModalOpen,
  ] = useState<boolean>(false);
  const [isGoogleReviewApiKeyModalOpen, setIsGoogleReviewApiKeyModalOpen] =
    useState<boolean>(false);
  const [
    isTrustpilotReviewApiKeyModalOpen,
    setIsTrustpilotReviewApiKeyModalOpen,
  ] = useState<boolean>(false);

  // NOTIFICATION MODALS:
  const [isActualVisitorsModalOpen, setIsActualVisitorsModalOpen] =
    useState<boolean>(false);
  const [isCustomNotificationModalOpen, setIsCustomNotificationModalOpen] =
    useState<boolean>(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState<boolean>(false);
  const [
    isRegistrationNotificationModalOpen,
    setIsRegistrationNotificationModalOpen,
  ] = useState<boolean>(false);
  const [
    isLastOrdersNotificationModalOpen,
    setIsLastOrdersNotificationModalOpen,
  ] = useState<boolean>(false);
  const [isHeurekaNotificationModalOpen, setIsHeurekaNotificationModalOpen] =
    useState<boolean>(false);
  const [isZboziNotificationModalOpen, setIsZboziNotificationModalOpen] =
    useState<boolean>(false);
  const [
    isShoptetLastOrdersNotificationModalOpen,
    setIsShoptetLastOrdersNotificationModalOpen,
  ] = useState<boolean>(false);
  const [
    isShopifyLastOrdersNotificationModalOpen,
    setIsShopifyLastOrdersNotificationModalOpen,
  ] = useState<boolean>(false);
  const [
    isEshopRychleLastOrdersNotificationModalOpen,
    setIsEshopRychleLastOrdersNotificationModalOpen,
  ] = useState<boolean>(false);
  const [
    isShoptetCouponNotificationModalOpen,
    setIsShoptetCouponNotificationModalOpen,
  ] = useState<boolean>(false);
  const [
    isSpolehliveRecenzeNotificationModalOpen,
    setIsSpolehliveRecenzeNotificationModalOpen,
  ] = useState<boolean>(false);
  const [
    isGoogleReviewNotificationModalOpen,
    setIsGoogleReviewNotificationModalOpen,
  ] = useState<boolean>(false);
  const [
    isTrustpilotReviewNotificationModalOpen,
    setIsTrustpilotReviewNotificationModalOpen,
  ] = useState<boolean>(false);

  const [notificationsPreviewsStrings, setNotificationsPreviewsStrings] =
    useState<NotificationsPreviewsStringsProps>({
      lastOrders: "",
      lastRegistrations: "",
      actualVisitors: "",
      visitorSummary: "",
      registrationSummary: "",
      orderSummary: "",
      heureka: "",
      zboziCompanyRating: "",
      zboziProductRating: "",
      individual: "",
      shoptetLastOrders: "",
      shopifyLastOrders: "",
      eshopRychleLastOrders: "",
      shoptetCouponCode: "",
      spolehlivaRecenzeShopRating: "",
      spolehlivaRecenzeProductRating: "",
      googleReviews: "",
      trustpilotReviews: "",
    });

  useEffect(() => {
    const fetchSiteData = async () => {
      if (webId) {
        try {
          const _websiteManager = WebsiteManager.getManager();
          const result: WebsiteDto =
            await _websiteManager.getWebsiteSingleAsync(webId);
          setWebsiteDto(result);
        } catch (e: any) {
          showFlashMessageWithTimeout(dispatch, e.toString(), "error");
        }
      }
    };

    fetchSiteData();
  }, [id, webId, dispatch, apiKeyModalOpenFor]);

  useLayoutEffect(() => {
    const getReview = async () => {
      // Use website language for previews, fallback to UI language
      const previewLanguage = websiteDto?.language ?? i18n.language;

      const result = await generateNotificationPreviews(
        t,
        previewLanguage,
        {
          brandingIsShowingIcon: showIcon,
          activeSize,
          backgroundColor,
          backgroundOpacity,
          borderRadius,
          iconBackgroundColor,
          iconBackgroundRadius,
          iconColor,
          textColor,
        },
        customWebBranding,
      );
      setNotificationsPreviewsStrings(result);
    };
    getReview();
  }, [
    t,
    showIcon,
    activeSize,
    backgroundColor,
    backgroundOpacity,
    borderRadius,
    iconBackgroundColor,
    iconBackgroundRadius,
    iconColor,
    textColor,
    customWebBranding,
    i18n.language,
    websiteDto?.language,
  ]);

  const openModalForNotificationType = (type: NotificationType) => {
    switch (type) {
      case "heureka":
        setIsHeurekaNotificationModalOpen(true);
        break;
      case "actualVisitors":
        setIsActualVisitorsModalOpen(true);
        break;
      case "individual":
        setIsCustomNotificationModalOpen(true);
        break;
      case "lastRegistrations":
        setIsRegistrationNotificationModalOpen(true);
        break;
      case "lastOrders":
        setIsLastOrdersNotificationModalOpen(true);
        break;
      case "visitorSummary":
        setIsSummaryModalOpen(true);
        break;
      case "zboziCompanyRating":
        setIsZboziNotificationModalOpen(true);
        break;
      case "spolehlivaRecenzeShopRating":
        setIsSpolehliveRecenzeNotificationModalOpen(true);
        break;
      case "shoptetLastOrders":
        setIsShoptetLastOrdersNotificationModalOpen(true);
        break;
      case "shopifyLastOrders":
        setIsShopifyLastOrdersNotificationModalOpen(true);
        break;
      case "eshopRychleLastOrders":
        setIsEshopRychleLastOrdersNotificationModalOpen(true);
        break;
      case "shoptetCouponCode":
        setIsShoptetCouponNotificationModalOpen(true);
        break;
      case "googleReviews":
        setIsGoogleReviewNotificationModalOpen(true);
        break;
      case "trustpilotReviews":
        setIsTrustpilotReviewNotificationModalOpen(true);
        break;
      default:
        break;
    }
  };

  const deleteNotificationIfNotSaved = async () => {
    // Use ref to check if notification was saved (avoids React batching issues)
    if (isNotificationAdded && !wasNotificationSavedRef.current && createdNotificationId) {
      try {
        await NotificationManager.getManager().deleteNotificationSettings(
          createdNotificationId,
        );
      } catch (err: any) {
        console.error("Failed to delete unsaved notification:", err);
      }
    }
    setIsNotificationAdded(false);
    wasNotificationSavedRef.current = false;
  };

  const onNotificationSaveSuccess = () => {
    // Set ref immediately (no batching delay)
    wasNotificationSavedRef.current = true;
    showFlashMessageWithTimeout(
      dispatch,
      t("popups.notificationAdded"),
      "success",
    );
    setIsNotificationAdded(false);
  };

  const createNotification = async (type: NotificationType) => {
    const createNotificationResponse = await createNotificationPrimitive(type, {
      updateState: true,
      useDefaults: true,
    });

    setCreatedNotificationId(createNotificationResponse?.id ?? "");
    openModalForNotificationType(type);
    setIsNotificationAdded(true);
    wasNotificationSavedRef.current = false;
  };

  const onAddHeurekaApiKey = (apiKey: string) => {
    if (websiteDto) {
      setWebsiteDto({ ...websiteDto, heurekaApiKey: apiKey });
      createNotification("heureka");
    }
  };

  const onAddZboziApiKey = (apiKey: string) => {
    if (websiteDto) {
      setWebsiteDto({ ...websiteDto, zboziApiKey: apiKey });
      createNotification("zboziCompanyRating");
    }
  };

  const onSpolehliveRecenzeLogin = (shopId: string, password: string) => {
    if (websiteDto) {
      setWebsiteDto({
        ...websiteDto,
        spolehlivaRecenzeShopId: shopId,
        spolehlivaRecenzeShopPassword: password,
      });
      createNotification("spolehlivaRecenzeShopRating");
    }
  };

  const onAddGoogleReviewApiKey = (placeId: string) => {
    if (websiteDto) {
      setWebsiteDto({ ...websiteDto, googleReviewsPlaceId: placeId });
      createNotification("googleReviews");
    }
  };

  const onAddTrustpilotReviewBussinessId = (businessId: string) => {
    if (websiteDto) {
      setWebsiteDto({ ...websiteDto, trustpilotBusinessUnitId: businessId });
      createNotification("trustpilotReviews");
    }
  };

  return (
    <Box style={{ flex: 1 }}>
      <Stack
        direction='row'
        sx={{
          width: 1,
          justifyContent: "space-between",
          alignItems: "center",
          boxSizing: "border-box",
          px: 4,
          pt: 2,
        }}
      >
        <Typography
          sx={{
            color: theme.palette.main.primary.o,
            fontSize: 16,
            cursor: "pointer",
            userSelect: "none",
          }}
          onClick={() => navigate(-1)}
        >
          {t("back")}
        </Typography>
        {detectedChange && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              backgroundColor: theme.palette.background.paper,
              zIndex: theme.zIndex.appBar,
            }}
          >
            <RestartAltIcon
              sx={{ cursor: "pointer" }}
              onClick={() => setReset(true)}
            />
            <CustomButton
              size='small'
              type='primary'
              title={t("saveChanges")}
              style={{
                padding: "0 8px",
              }}
              onClick={() => setSave(true)}
            />
          </Box>
        )}
      </Stack>
      <Typography fontStyle='italic' sx={{ px: "28px", py: 2 }}>
        {t("campaign.notifications.addNotificationHelperText")}
      </Typography>
      <NotificationTypeSelector
        createNotification={createNotification}
        notificationsPreviewsStrings={notificationsPreviewsStrings}
        setApiKeyModalOpenFor={setApiKeyModalOpenFor}
        websiteDto={websiteDto}
      />

      {websiteDto?.id && (
        <HeurekaApiKeyModal
          websiteData={websiteDto}
          createdWebId={websiteDto?.id}
          isOpen={isAddZboziApiKeyModalOpen}
          onClose={() => {
            setIsAddZboziApiKeyModalOpen(false);
            setIsNotificationAdded(false);
          }}
          onAddKey={onAddHeurekaApiKey}
        />
      )}

      {websiteDto?.id && (
        <ZboziApiKeyModal
          websiteData={websiteDto}
          createdWebId={websiteDto?.id}
          isOpen={addZboziApiKeyModal}
          onClose={() => {
            setAddZboziApiKeyModal(false);
            isNotificationAdded &&
              showFlashMessageWithTimeout(
                dispatch,
                t("popups.notificationAdded"),
                "success",
              );
            setIsNotificationAdded(false);
          }}
          onAddKey={onAddZboziApiKey}
        />
      )}

      {websiteDto?.id && (
        <SpolehliveRecenzeLoginModal
          websiteData={websiteDto}
          createdWebId={websiteDto?.id}
          isOpen={isSpolehliveRecenzeLoginModalOpen}
          onClose={() => {
            setIsSpolehliveRecenzeLoginModalOpen(false);
            isNotificationAdded &&
              showFlashMessageWithTimeout(
                dispatch,
                t("popups.notificationAdded"),
                "success",
              );
            setIsNotificationAdded(false);
          }}
          onAddKey={onSpolehliveRecenzeLogin}
        />
      )}

      {websiteDto?.googleReviewsPlaceId && (
        <GoogleReviewApiKeyModal
          websiteData={websiteDto}
          createdWebId={websiteDto?.id}
          isOpen={isGoogleReviewApiKeyModalOpen}
          onClose={() => {
            setIsGoogleReviewApiKeyModalOpen(false);
            isNotificationAdded &&
              showFlashMessageWithTimeout(
                dispatch,
                t("popups.notificationAdded"),
                "success",
              );
            setIsNotificationAdded(false);
          }}
          onAddKey={onAddGoogleReviewApiKey}
        />
      )}

      {websiteDto?.trustpilotBusinessUnitId && (
        <TrustPilotApiKeyModal
          websiteData={websiteDto}
          createdWebId={websiteDto?.id}
          isOpen={isTrustpilotReviewApiKeyModalOpen}
          onClose={() => {
            setIsTrustpilotReviewApiKeyModalOpen(false);
            isNotificationAdded &&
              showFlashMessageWithTimeout(
                dispatch,
                t("popups.notificationAdded"),
                "success",
              );
            setIsNotificationAdded(false);
          }}
          onAddKey={onAddTrustpilotReviewBussinessId}
        />
      )}

      <HeurekaNotificationModal
        createdNotificationId={createdNotificationId}
        isOpen={isHeurekaNotificationModalOpen}
        forOrders={isLastOrdersNotificationModalOpen}
        branding={{
          brandingIsShowingIcon: showIcon,
          activeSize,
          backgroundColor,
          backgroundOpacity,
          borderRadius,
          iconBackgroundColor,
          iconBackgroundRadius,
          iconColor,
          textColor,
        }}
        onClose={async () => {
          await deleteNotificationIfNotSaved();
          setIsHeurekaNotificationModalOpen(false);
        }}
        onSaveSuccess={onNotificationSaveSuccess}
      />

      <ZboziNotificationModal
        createdNotificationId={createdNotificationId}
        branding={{
          brandingIsShowingIcon: showIcon,
          activeSize,
          backgroundColor,
          backgroundOpacity,
          borderRadius,
          iconBackgroundColor,
          iconBackgroundRadius,
          iconColor,
          textColor,
        }}
        forOrders={isLastOrdersNotificationModalOpen}
        isOpen={isZboziNotificationModalOpen}
        onClose={async () => {
          await deleteNotificationIfNotSaved();
          setIsZboziNotificationModalOpen(false);
        }}
        onSaveSuccess={onNotificationSaveSuccess}
      />

      <SpolehliveRecenzeNotificationModal
        createdNotificationId={createdNotificationId}
        isOpen={isSpolehliveRecenzeNotificationModalOpen}
        onClose={async () => {
          await deleteNotificationIfNotSaved();
          setIsSpolehliveRecenzeNotificationModalOpen(false);
        }}
        onSaveSuccess={onNotificationSaveSuccess}
      />

      <ApiKeyModal
        type={apiKeyModalOpenFor}
        isOpen={!!apiKeyModalOpenFor}
        onClose={() => {
          setApiKeyModalOpenFor(undefined);
          isNotificationAdded &&
            showFlashMessageWithTimeout(
              dispatch,
              t("popups.notificationAdded"),
              "success",
            );
          setIsNotificationAdded(false);
        }}
        onOpenModal={() => {
          if (apiKeyModalOpenFor === "heureka") {
            setIsAddZboziApiKeyModalOpen(true);
          } else if (apiKeyModalOpenFor === "spolehlivaRecenze") {
            setIsSpolehliveRecenzeLoginModalOpen(true);
          } else if (apiKeyModalOpenFor === "googleReviews") {
            setIsGoogleReviewApiKeyModalOpen(true);
          } else {
            setAddZboziApiKeyModal(true);
          }
          setApiKeyModalOpenFor(undefined);
        }}
      />

      <ActualVisitorsModal
        createdNotificationId={createdNotificationId}
        isOpen={isActualVisitorsModalOpen}
        onClose={async () => {
          await deleteNotificationIfNotSaved();
          setIsActualVisitorsModalOpen(false);
        }}
        onSaveSuccess={onNotificationSaveSuccess}
        language={language}
        branding={{
          brandingIsShowingIcon: showIcon,
          activeSize,
          backgroundColor,
          backgroundOpacity,
          borderRadius,
          iconBackgroundColor,
          iconBackgroundRadius,
          iconColor,
          textColor,
        }}
      />

      <CustomNotificationModal
        createdNotificationId={createdNotificationId}
        isOpen={isCustomNotificationModalOpen}
        onClose={async () => {
          await deleteNotificationIfNotSaved();
          setIsCustomNotificationModalOpen(false);
        }}
        onSaveSuccess={onNotificationSaveSuccess}
        branding={{
          brandingIsShowingIcon: showIcon,
          activeSize,
          backgroundColor,
          backgroundOpacity,
          borderRadius,
          iconBackgroundColor,
          iconBackgroundRadius,
          iconColor,
          textColor,
          logoDisplay: customWebBranding?.logoDisplay,
          logo: customWebBranding?.logo,
          logoBackgroundColor: customWebBranding?.logoBackgroundColor,
          logoText: customWebBranding?.logoText,
        }}
      />

      <SummaryModal
        createdNotificationId={createdNotificationId}
        isOpen={isSummaryModalOpen}
        onClose={async () => {
          await deleteNotificationIfNotSaved();
          setIsSummaryModalOpen(false);
        }}
        onSaveSuccess={onNotificationSaveSuccess}
        language={language}
        branding={{
          brandingIsShowingIcon: showIcon,
          activeSize,
          backgroundColor,
          backgroundOpacity,
          borderRadius,
          iconBackgroundColor,
          iconBackgroundRadius,
          iconColor,
          textColor,
        }}
      />

      <ShoptetLastOrdersNotificationModal
        createdNotificationId={createdNotificationId}
        isOpen={isShoptetLastOrdersNotificationModalOpen}
        onClose={async () => {
          await deleteNotificationIfNotSaved();
          setIsShoptetLastOrdersNotificationModalOpen(false);
        }}
        onSaveSuccess={onNotificationSaveSuccess}
        branding={{
          brandingIsShowingIcon: showIcon,
          activeSize,
          backgroundColor,
          backgroundOpacity,
          borderRadius,
          iconBackgroundColor,
          iconBackgroundRadius,
          iconColor,
          textColor,
        }}
        language={language}
      />

      <ShoptetCouponCodeNotificationModal
        createdNotificationId={createdNotificationId}
        isOpen={isShoptetCouponNotificationModalOpen}
        onClose={async () => {
          await deleteNotificationIfNotSaved();
          setIsShoptetCouponNotificationModalOpen(false);
        }}
        onSaveSuccess={onNotificationSaveSuccess}
        language={language}
        branding={{
          brandingIsShowingIcon: showIcon,
          activeSize,
          backgroundColor,
          backgroundOpacity,
          borderRadius,
          iconBackgroundColor,
          iconBackgroundRadius,
          iconColor,
          textColor,
        }}
      />

      <ShopifyLastOrdersNotificationModal
        createdNotificationId={createdNotificationId}
        isOpen={isShopifyLastOrdersNotificationModalOpen}
        onClose={async () => {
          await deleteNotificationIfNotSaved();
          setIsShopifyLastOrdersNotificationModalOpen(false);
        }}
        onSaveSuccess={onNotificationSaveSuccess}
        language={language}
        branding={{
          brandingIsShowingIcon: showIcon,
          activeSize,
          backgroundColor,
          backgroundOpacity,
          borderRadius,
          iconBackgroundColor,
          iconBackgroundRadius,
          iconColor,
          textColor,
        }}
      />

      <EshopRychleLastOrdersNotificationModal
        createdNotificationId={createdNotificationId}
        isOpen={isEshopRychleLastOrdersNotificationModalOpen}
        onClose={async () => {
          await deleteNotificationIfNotSaved();
          setIsEshopRychleLastOrdersNotificationModalOpen(false);
        }}
        onSaveSuccess={onNotificationSaveSuccess}
        language={language}
        branding={{
          brandingIsShowingIcon: showIcon,
          activeSize,
          backgroundColor,
          backgroundOpacity,
          borderRadius,
          iconBackgroundColor,
          iconBackgroundRadius,
          iconColor,
          textColor,
        }}
      />

      <GoogleReviewNotificationModal
        createdNotificationId={createdNotificationId}
        isOpen={isGoogleReviewNotificationModalOpen}
        onClose={async () => {
          await deleteNotificationIfNotSaved();
          setIsGoogleReviewNotificationModalOpen(false);
        }}
        onSaveSuccess={onNotificationSaveSuccess}
        branding={{
          brandingIsShowingIcon: showIcon,
          activeSize,
          backgroundColor,
          backgroundOpacity,
          borderRadius,
          iconBackgroundColor,
          iconBackgroundRadius,
          iconColor,
          textColor,
        }}
      />

      <TrustPilotNotificationModal
        createdNotificationId={createdNotificationId}
        isOpen={isTrustpilotReviewNotificationModalOpen}
        onClose={async () => {
          await deleteNotificationIfNotSaved();
          setIsTrustpilotReviewNotificationModalOpen(false);
        }}
        onSaveSuccess={onNotificationSaveSuccess}
        branding={{
          brandingIsShowingIcon: showIcon,
          activeSize,
          backgroundColor,
          backgroundOpacity,
          borderRadius,
          iconBackgroundColor,
          iconBackgroundRadius,
          iconColor,
          textColor,
        }}
      />

      {(isRegistrationNotificationModalOpen ||
        isLastOrdersNotificationModalOpen) && (
        <OrdersAndRegistrationsModal
          forOrders={isLastOrdersNotificationModalOpen}
          createdNotificationId={createdNotificationId}
          branding={{
            brandingIsShowingIcon: showIcon,
            activeSize,
            backgroundColor,
            backgroundOpacity,
            borderRadius,
            iconBackgroundColor,
            iconBackgroundRadius,
            iconColor,
            textColor,
          }}
          isOpen={
            isRegistrationNotificationModalOpen ||
            isLastOrdersNotificationModalOpen
          }
          onClose={async () => {
            await deleteNotificationIfNotSaved();
            setIsRegistrationNotificationModalOpen(false);
            setIsLastOrdersNotificationModalOpen(false);
          }}
          onSaveSuccess={onNotificationSaveSuccess}
          language={language}
        />
      )}
    </Box>
  );
};

export default AddNotification;
